import { useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router";
import { FormProvider } from "react-hook-form";

import { Grid, Stack, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";

import { useFormWithNotification } from "@covertree/hooks";
import FormFileField from "@covertree/ui/forms/FormFileField";
import { Button, Form } from "@covertree/ui";
import DocumentItem from "@covertree/ui/content/DocumentItem";

import formatPageTitle from "@utils/formatPageTitle";
import { MAX_FILE_SIZE } from "@consts/files";
import { SEQUOIA_STATUSES_PATH } from "@routing/paths";

import { SequoiaUploadForm, defaultValues } from "./form";
import { useUploadSequoiaFiles } from "./useUploadSequoiaFiles";
import { SequoiaUploadResult } from "./SequoiaUploadResult";

export function SequoiaUpload() {
  const navigate = useNavigate();

  const form = useFormWithNotification<SequoiaUploadForm>({
    defaultValues,
  });

  const { handleSubmit: submit, watch, reset: resetForm } = form;
  const files = watch("files") || [];

  const goToSequoiaStatuses = useCallback(() => {
    navigate(`/${SEQUOIA_STATUSES_PATH}`);
  }, []);

  const resetFormValues = useCallback(
    () => resetForm({ files: [] }),
    [resetForm]
  );

  const { loading, handleSubmit, uploadResult } =
    useUploadSequoiaFiles(resetFormValues);

  return (
    <>
      <Helmet>
        <title>{formatPageTitle("Sequoia Upload")}</title>
      </Helmet>
      <Typography variant="h3" gutterBottom>
        Sequoia Upload
      </Typography>
      <Typography gutterBottom variant="h6">
        Here you can upload Sequoia Platform input files to create quotes for a
        book conversion. The allowed file type is a .csv. Any failure reports
        will be available on the linked Sequoia Statuses page. The process will
        start automatically when the file uploads successfully. If the number of
        policies is high, it might take a while for all of them to be created.
        All created quotes will be visible on the Sequoia quotes list page.
      </Typography>
      <Form onSubmit={submit(handleSubmit)}>
        <FormProvider {...form}>
          <Stack direction="column" spacing={4}>
            <FormFileField
              name="files"
              accept={{
                "text/csv": [".csv"],
              }}
              maxFileSize={MAX_FILE_SIZE}
              maxFileAmount={1}
              disabled={loading}
            />
            {files.map((file: File, index) => (
              <DocumentItem
                key={`${file.name}-${index}`}
                name={file.name}
                actions={[
                  {
                    name: "Remove",
                    onClick: resetFormValues,
                    icon: <Delete />,
                    disabled: loading,
                  },
                ]}
              />
            ))}
            <Grid container gap={2}>
              <Button
                variant="contained"
                type="submit"
                loading={loading}
                disabled={!files.length || loading}>
                Submit
              </Button>
              <Button
                variant="contained"
                type="button"
                disabled={loading}
                onClick={goToSequoiaStatuses}>
                Go to Sequoia Statuses
              </Button>
            </Grid>
          </Stack>
        </FormProvider>
      </Form>

      {uploadResult && <SequoiaUploadResult uploadResult={uploadResult} />}
    </>
  );
}
